import React from 'react';

import type { IntlShape } from 'react-intl-next';
import Loadable from 'react-loadable';

import {
	DefaultExtensionProvider,
	type ExtensionParams,
	type MultiBodiedExtensionActions,
} from '@atlaskit/editor-common/extensions';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import {
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
	SECTION_EXTENSION_TYPE,
} from '@atlassian/company-hub-common';

import {
	DEFAULT_BACKGROUND_COLOR,
	DEFAULT_CONTENT_WIDTH,
	SECTION_EXTENSION_KEY,
	type SectionParameters,
	type SectionProps,
} from '../../index';

import { messages } from './messages';

const SectionExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_SectionExtension" */ '../section-extension'
			)
		).SectionExtension,
	loading: () => <UFOLoadHold name="section-extension-loader"></UFOLoadHold>,
});

const sectionParameters = (intl: IntlShape): SectionParameters => ({
	extensionTitle: intl.formatMessage(messages.lozengeTitle),
	backgroundColor: DEFAULT_BACKGROUND_COLOR,
	contentWidth: DEFAULT_CONTENT_WIDTH,
});

export type SectionExtensionProps = SectionProps & {
	getSectionExtensionContents: (...params: any) => any[];
};

export const sectionExtensionProvider =
	({ getSectionExtensionContents, mode, propsProvider }: SectionExtensionProps) =>
	async ({
		contentId,
		editorActions,
		openConfigPanel,
		intl,
	}: CustomSitesExtensionProviderProps) => {
		const title = intl.formatMessage(messages.title);
		const description = intl.formatMessage(messages.description);
		const parameters = sectionParameters(intl);
		const type = SECTION_EXTENSION_TYPE;
		const key = SECTION_EXTENSION_KEY;
		const initialContentText = {
			header: intl.formatMessage(messages.contentHeader),
			subHeader: intl.formatMessage(messages.contentSubHeader),
			placeholderTitle: intl.formatMessage(messages.placeHolderTitle),
		};

		const icons = {
			'48': () =>
				import(
					/* webpackChunkName: "@atlaskit-internal_atlaskitIconCorePremium" */ '@atlaskit/icon/core/premium'
				).then(({ default: PremiumIcon }) => () => (
					<PremiumIcon color={token('color.icon.subtle')} label="" spacing="spacious" />
				)),
		};

		const update = async () => {
			const selectedNode = editorActions?.getSelectedNode()?.toJSON();
			const selectedLocalId = selectedNode?.attrs?.localId;

			if (!!selectedLocalId && openConfigPanel) {
				openConfigPanel(selectedLocalId);
			}
		};

		const render =
			async () =>
			({
				node,
				actions,
			}: {
				actions: MultiBodiedExtensionActions;
				node: ExtensionParams<SectionParameters>;
			}) => {
				return (
					<SectionExtension
						contentId={contentId}
						extensionNode={node}
						editorActions={editorActions}
						mbeActions={actions}
						mode={mode}
						propsProvider={propsProvider}
					/>
				);
			};

		const additionalModuleNodeProps = {
			__allowBodiedOverride: true,
		};

		return new DefaultExtensionProvider([
			getExtensionManifest<SectionParameters>({
				key,
				icons,
				parameters,
				update,
				render,
				title,
				type,
				description,
				source: 'premiumSectionInsert',
				nodeType: 'multiBodiedExtension',
				quickInsertAction: async () =>
					Promise.resolve({
						type: 'multiBodiedExtension',
						key: 'hub-section',
						attrs: {
							extensionType: type,
							extensionKey: 'hub-section',
							layout: 'full-width',
							parameters,
						},
						content: getSectionExtensionContents(initialContentText),
					}),
				additionalModuleNodeProps,
			}),
		]);
	};
